import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { ArrowUpRight, Gauge, Layers, RefreshCw, ServerOff, ShoppingCart, Wallet } from "lucide-react";
import {
  getAdminCollections, getAdminOrders, getAdminWallets, getAdminWithdrawals, getChainOverview,
} from "../../lib/investAdminApi";
import BlockchainOverview from "../../components/admin/blockchain/BlockchainOverview";
import BlockchainCollections from "../../components/admin/blockchain/BlockchainCollections";
import BlockchainOrders from "../../components/admin/blockchain/BlockchainOrders";
import BlockchainWithdrawals from "../../components/admin/blockchain/BlockchainWithdrawals";
import BlockchainWallets from "../../components/admin/blockchain/BlockchainWallets";
import {
  CopyHex, EmptyState, LOW_GAS_ETH, StatusBadge, collectionStatus, fmtNum,
} from "../../components/admin/blockchain/bcShared";

const SUB_TABS = [
  { id: "overview", label: "Overview", icon: Gauge },
  { id: "collections", label: "Collections", icon: Layers },
  { id: "orders", label: "Orders", icon: ShoppingCart },
  { id: "withdrawals", label: "Withdrawals", icon: ArrowUpRight },
  { id: "wallets", label: "Wallets", icon: Wallet },
];

const SECTIONS = [
  ["collections", getAdminCollections],
  ["orders", getAdminOrders],
  ["withdrawals", getAdminWithdrawals],
  ["wallets", getAdminWallets],
];

function NetworkBar({ overview, loading, refreshedAt, onRefresh }) {
  const c = overview?.contract;
  const healthy = overview?.services?.chain && overview?.services?.wallet && c;
  return (
    <div className="zc-card px-4 sm:px-5 py-3 mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs" data-testid="bc-network-bar">
      <div className="flex items-center gap-2 font-semibold">
        <span
          className={"w-2 h-2 rounded-full " + (healthy ? "bg-[var(--zc-success)]" : overview ? "bg-[var(--zc-danger)]" : "bg-[var(--zc-border-2)]")}
          aria-hidden
        />
        {healthy ? "Network live" : overview ? "Degraded" : "Connecting…"}
      </div>
      {c && (
        <>
          <span className="text-[var(--zc-text-2)]">Hardhat local · chain <b className="tabular-nums">{c.chainId}</b></span>
          <span className="text-[var(--zc-text-2)]">Block <b className="tabular-nums">#{fmtNum(c.blockNumber)}</b></span>
          <span className="flex items-center gap-1.5 text-[var(--zc-text-2)]">Contract <CopyHex value={c.contractAddress} label="Contract address" /></span>
          {c.paused && <StatusBadge tone="danger">Paused</StatusBadge>}
        </>
      )}
      {overview && (!overview.services.chain || !overview.services.wallet) && (
        <span className="text-[var(--zc-danger)]">
          {[!overview.services.chain && "chain-service", !overview.services.wallet && "wallet-service"].filter(Boolean).join(" & ")} offline
        </span>
      )}
      <div className="ml-auto flex items-center gap-3">
        {refreshedAt && <span className="text-[var(--zc-text-3)] hidden sm:inline">Updated {format(refreshedAt, "HH:mm:ss")}</span>}
        <button type="button" onClick={onRefresh} disabled={loading} className="zc-btn-ghost px-3 h-8 text-xs font-semibold inline-flex items-center gap-1.5" data-testid="bc-refresh">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
    </div>
  );
}

export default function AdminBlockchainTab({ initialSubTab }) {
  const [sub, setSub] = useState(SUB_TABS.some((t) => t.id === initialSubTab) ? initialSubTab : "overview");
  const [overview, setOverview] = useState(null);
  const [data, setData] = useState({ collections: [], orders: [], withdrawals: [], wallets: [] });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [ov, ...rest] = await Promise.allSettled([getChainOverview(), ...SECTIONS.map(([, fn]) => fn())]);
    setOverview(ov.status === "fulfilled" ? ov.value : { services: { chain: false, wallet: false }, contract: null, contractError: ov.reason?.message });
    const nextErr = {};
    setData((prev) => {
      const next = { ...prev };
      rest.forEach((r, i) => {
        const key = SECTIONS[i][0];
        if (r.status === "fulfilled") next[key] = Array.isArray(r.value) ? r.value : [];
        else nextErr[key] = r.reason?.message || `Could not load ${key}`;
      });
      return next;
    });
    setErrors(nextErr);
    setRefreshedAt(new Date());
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const offline = overview && !overview.services.chain && !overview.services.wallet;
  const paused = !!overview?.contract?.paused;

  // Per-tab badge: count + whether something there needs attention.
  const meta = {
    collections: { count: data.collections.length, issue: data.collections.some((c) => collectionStatus(c).tone === "danger") },
    orders: { count: data.orders.length, issue: data.orders.some((o) => o.status === "TRANSFER_FAILED") },
    withdrawals: { count: data.withdrawals.length, issue: data.withdrawals.some((w) => w.status === "FAILED") },
    wallets: { count: data.wallets.length, issue: data.wallets.some((w) => w.ethBalance != null && Number(w.ethBalance) < LOW_GAS_ETH) },
  };

  const section = (key) => ({ loading, error: errors[key], onRetry: load });

  return (
    <div data-testid="admin-blockchain">
      <NetworkBar overview={overview} loading={loading} refreshedAt={refreshedAt} onRefresh={load} />

      <nav className="mt-4 flex overflow-x-auto no-scrollbar gap-1 p-1 rounded-full bg-[var(--zc-bg-3)] w-full sm:w-fit" aria-label="Blockchain sections">
        {SUB_TABS.map(({ id, label, icon: Icon }) => {
          const active = sub === id;
          const m = meta[id];
          return (
            <button
              key={id}
              type="button"
              onClick={() => setSub(id)}
              aria-current={active ? "page" : undefined}
              className={
                "relative px-4 h-9 rounded-full text-xs font-semibold whitespace-nowrap inline-flex items-center gap-1.5 transition-colors "
                + (active ? "bg-[var(--zc-bg-2)] text-[var(--zc-secondary)] shadow-sm" : "text-[var(--zc-text-2)] hover:text-[var(--zc-text)]")
              }
              data-testid={`bc-subtab-${id}`}
            >
              <Icon size={13} />
              {label}
              {m?.count > 0 && <span className="tabular-nums opacity-60">{m.count}</span>}
              {m?.issue && <span className="w-1.5 h-1.5 rounded-full bg-[var(--zc-danger)]" aria-label="needs attention" />}
            </button>
          );
        })}
      </nav>

      <div className="mt-4">
        {offline ? (
          <div className="zc-card">
            <EmptyState
              icon={ServerOff}
              title="Investment services are offline"
              hint="The chain-service and wallet-service aren't reachable. Start them with `docker compose up -d`, then refresh."
              action={<button type="button" onClick={load} className="zc-btn-secondary px-4 h-9 text-xs font-semibold">Retry</button>}
            />
          </div>
        ) : (
          <>
            {sub === "overview" && (
              <BlockchainOverview overview={overview} {...data} onNavigate={setSub} onChanged={load} />
            )}
            {sub === "collections" && (
              <BlockchainCollections collections={data.collections} paused={paused} onChanged={load} {...section("collections")} />
            )}
            {sub === "orders" && <BlockchainOrders orders={data.orders} {...section("orders")} />}
            {sub === "withdrawals" && (
              <BlockchainWithdrawals withdrawals={data.withdrawals} collections={data.collections} {...section("withdrawals")} />
            )}
            {sub === "wallets" && (
              <BlockchainWallets wallets={data.wallets} orders={data.orders} onChanged={load} {...section("wallets")} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
